import type { ClassifiedSpecificEntries } from '../../AppAndSiteMarketplace.types';

/** Common first segments (TLD-like) of an Android package name. Any 2-letter ccTLD is also accepted. */
export const ANDROID_BUNDLE_ALLOWED_PREFIXES = [
  'com',
  'org',
  'net',
  'io',
  'app',
  'dev',
  'me',
  'tv',
  'co',
  'info',
  'biz',
  'edu',
  'gov'
] as const;

const KEY_ENTRY_REGEX = /^[^\s:]+::\S+$/;
const IOS_BUNDLE_REGEX = /^(id)?\d{6,12}$/i;
const ANDROID_BUNDLE_REGEX = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/i;
const COUNTRY_CODE_REGEX = /^[a-z]{2}$/;

/**
 * Key or app bundle in the `text::text` format, e.g. `55102::zz_ue_example_iphone` or `bundle::com.whatsapp`.
 * The first part has no whitespace nor `:`; the second part has no whitespace but may contain `:`.
 */
export const isKeyEntry = (entry: string): boolean => KEY_ENTRY_REGEX.test(entry);

/** App Store id, with or without the `id` prefix, e.g. `389801252` or `id389801252`. */
export const isIosBundle = (entry: string): boolean => IOS_BUNDLE_REGEX.test(entry);

/** Reverse-domain package name whose first segment is a common package TLD, e.g. `com.whatsapp`. */
export const isAndroidBundle = (entry: string): boolean => {
  if (!ANDROID_BUNDLE_REGEX.test(entry)) {
    return false;
  }
  const prefix = entry.split('.')[0].toLowerCase();
  return COUNTRY_CODE_REGEX.test(prefix) || (ANDROID_BUNDLE_ALLOWED_PREFIXES as readonly string[]).includes(prefix);
};

/** Splits entries into valid (`text::text` key or app bundle) and invalid ones. Deduplicated, keeping the input order. */
export const classifySpecificEntries = (entries: string[]): ClassifiedSpecificEntries => {
  const valid: string[] = [];
  const invalid: string[] = [];

  for (const entry of new Set(entries)) {
    (isKeyEntry(entry) || isIosBundle(entry) || isAndroidBundle(entry) ? valid : invalid).push(entry);
  }

  return { invalid, valid };
};
